import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useReportColumnFilterDropdownValuesQuery } from "../../../api/network/reportApiServices";
import { ReportColumnFilterDropdownValuesResponseItem } from "../../../api/types/Report";
import svgIcons from "../../../assets/svg/svgIcons";
import {
  AdminFormFieldDropdown,
  AdminFormFieldSubmit,
} from "../../../components/admin/formFields";
import AppSearchBox from "../../../components/searchBox";
import TickCheckbox from "../../../components/tickCheckbox";
import { APP_CONFIG } from "../../../constants/constants";
import {
  REPORT_FIELD_OUTPUT_TYPES,
  REPORT_FILTER_OPERATOR_TYPES,
  REPORT_SPECIAL_VALUES,
} from "../constants";
import { getFieldSpecificFilterOptions } from "../util";
import { useCreateCustomReportContext } from "../context/createCustomReport.context";
import useConversion from "../../../hooks/conversion";
import { ReportTableColumn } from "../../../components/reports/reportTable";
import { AGGREGATE_FUNCTION } from "./popupReportColumnAggregation";

export type ApplyReportColumnFilterCallback = (
  identifier: string,
  fieldOutputType: REPORT_FIELD_OUTPUT_TYPES,
  selectedValues: string[]
) => void;

interface IPopupReportColumnFilterProps {
  column: ReportTableColumn;
  showSelectableValues: boolean;
  fieldId: string;
  identifier: string;
  isLast?: boolean;
  onApplyReportColumnFilter: ApplyReportColumnFilterCallback;
  reportId: number;
  selected?: string[];
  wrapperClass?: string;
  isAdditionalDropdown?: boolean;
  showResetButton?: boolean;
  handlePopup?: (column?: ReportTableColumn, index?: number) => void;
  index?: number;
}

const PopupReportColumnFilter = ({
  column,
  showSelectableValues = false,
  fieldId,
  identifier,
  isLast = false,
  onApplyReportColumnFilter,
  reportId,
  selected,
  handlePopup,
  index,
  wrapperClass = "absolute top-full mt-1 flex flex-col gap-2 py-2 bg-white border border-border-gray rounded-lg shadow-lg w-64 z-popup",
  isAdditionalDropdown = true,
  showResetButton = true,
}: IPopupReportColumnFilterProps) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "reports.columnFilters",
  });
  const { t: tFilterOptions } = useTranslation("translation", {
    keyPrefix: "reports.columnFilters.filterOptions",
  });
  const { t: tMain } = useTranslation("translation");

  const { setColumnsSearchKeyword, columnsOperator, setColumnsOperator } =
    useCreateCustomReportContext();

  const {
    data: dataColumnFilterDropdownValues,
    isFetching: isFetchingColumnFilterDropdownValues,
  } = useReportColumnFilterDropdownValuesQuery(
    {
      fields: fieldId,
      report_id: reportId,
    },
    {
      skip: !fieldId || !reportId || !showSelectableValues,
    }
  );

  const [searchKeyword, setSearchKeyword] = useState("");
  const { convertToUnit, DisplayConvertedValue } = useConversion();
  const [selectedOptions, setSelectedOptions] = useState<string[]>(
    selected
      ?.filter((item) => !!item)
      .map((item) =>
        item === REPORT_SPECIAL_VALUES.BLANK.FOR_API // "BLANK"
          ? REPORT_SPECIAL_VALUES.BLANK.FOR_COMPARE // ""
          : item === REPORT_SPECIAL_VALUES.NULL.FOR_API // "NULL"
            ? `${REPORT_SPECIAL_VALUES.NULL.FOR_COMPARE}` // "null"
            : item
      ) || []
  );

  const itemsListRef = React.useRef<HTMLDivElement>(null);
  const [itemsToDisplay, setItemsToDisplay] = useState<
    ReportColumnFilterDropdownValuesResponseItem[]
  >([]);

  const getDisplayValue = (originalValue: string): string => {
    const valueMapping: Record<string, string> = {
      InMotion: "Driving",
      Moving: "Driving",
    };
    return valueMapping[originalValue] || originalValue;
  };
  const getConvertedDisplayValue = (originalValue: string) => {
    if (originalValue === REPORT_SPECIAL_VALUES.NULL.FOR_COMPARE)
      return REPORT_SPECIAL_VALUES.NULL.FOR_DISPLAY;
    if (originalValue === REPORT_SPECIAL_VALUES.BLANK.FOR_COMPARE)
      return REPORT_SPECIAL_VALUES.BLANK.FOR_DISPLAY;

    try {
      if (
        column.field_object?.aggregate_function === AGGREGATE_FUNCTION.COUNT ||
        !column.field_output_type
      ) {
        return getDisplayValue(originalValue);
      }

      const convertedData = convertToUnit(
        column.field_output_type,
        originalValue,
        column?.convert_to_whole_number
      );
      return getDisplayValue(convertedData.finalData);
    } catch (error) {
      return getDisplayValue(originalValue);
    }
  };

  const carryOutItemsFiltration = () => {
    const filteredData = isFetchingColumnFilterDropdownValues
      ? []
      : (
          (dataColumnFilterDropdownValues as ReportColumnFilterDropdownValuesResponseItem[]) ||
          []
        )
          .filter(
            (item) =>
              item?.[identifier] === REPORT_SPECIAL_VALUES.BLANK.FOR_COMPARE ||
              !!`${item?.[identifier]}`
          )
          .reduce(
            (
              acc: any[],
              option: ReportColumnFilterDropdownValuesResponseItem
            ) => {
              const originalValue = `${option?.[identifier]}` || "";
              const displayValue = getConvertedDisplayValue(originalValue);

              // Skip if doesn't match search
              if (
                !displayValue
                  .toLowerCase()
                  .includes(searchKeyword.toLowerCase()) &&
                !selectedOptions.includes(originalValue)
              )
                return acc;

              // Find existing item with same display value
              const existingIndex = acc.findIndex(
                (item: any) =>
                  getConvertedDisplayValue(`${item?.[identifier]}`) ===
                  displayValue
              );

              if (existingIndex >= 0) {
                // Combine values
                const existing = acc[existingIndex];
                if (!existing.combinedValues)
                  existing.combinedValues = [existing[identifier]];
                if (!existing.combinedValues.includes(originalValue))
                  existing.combinedValues.push(originalValue);
              } else {
                // Add new item
                const newItem = { ...option } as any;
                if (
                  displayValue !== originalValue &&
                  ["InMotion", "Moving"].includes(originalValue)
                ) {
                  newItem.combinedValues = [originalValue];
                }
                acc.push(newItem);
              }

              return acc;
            },
            []
          );

    setItemsToDisplay(filteredData);
  };

  useEffect(() => {
    carryOutItemsFiltration();
  }, [
    dataColumnFilterDropdownValues,
    isFetchingColumnFilterDropdownValues,
    searchKeyword,
    selectedOptions,
  ]);

  // on search keyword change scroll to top
  useEffect(() => {
    if (itemsListRef.current) {
      itemsListRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [searchKeyword]);

  const [showAdditionalDropdown, setShowAdditionalDropdown] = useState(false);

  const FieldSpecificFilterOptions = useCallback(() => {
    const optionsDropdownValues = !!column.field_output_type
      ? getFieldSpecificFilterOptions(column.field_output_type)
      : [];
    return (
      <div className="px-2 text-left">
        <AdminFormFieldDropdown
          label={""}
          name="text_filter"
          id={`field_specific_filter_${fieldId}`}
          value={columnsOperator?.[fieldId] || ""}
          options={optionsDropdownValues?.map((option) => {
            return {
              value: option,
              label: tFilterOptions(option),
            };
          })}
          onChange={(e) => {
            setColumnsOperator &&
              !!e?.value &&
              setColumnsOperator((prev) => {
                // if previous value was between or new value is between, clear the search keyword
                // this prevents app from crashing when user switches between operators
                if (
                  prev[fieldId] === REPORT_FILTER_OPERATOR_TYPES.BETWEEN ||
                  e.value === REPORT_FILTER_OPERATOR_TYPES.BETWEEN
                ) {
                  setColumnsSearchKeyword &&
                    setColumnsSearchKeyword((prev) => ({
                      ...prev,
                      [fieldId]: "",
                    }));
                }
                handlePopup?.(column, index);
                return {
                  ...prev,
                  [fieldId]: e.value,
                };
              });
          }}
          menuPlacement={"bottom"}
        />
      </div>
    );
  }, [column.field_output_type, columnsOperator, fieldId]);

  return (
    <div className={`${wrapperClass} ${isLast ? "right-0" : "left-0"}`}>
      {/* {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
        <pre className="absolute top-2 right-2 text-[8px] max-h-72 overflow-auto text-left z-10">
          {JSON.stringify(dataColumnFilterDropdownValues, null, 2)}
        </pre>
      )} */}

      {showSelectableValues ? (
        <>
          {/* Additional Dropdown Button */}
          {isAdditionalDropdown && (
            <button
              type="button"
              className="flex items-center justify-between font-bold text-sm border-b px-2 h-10 border-black/10 hover:bg-gray-200"
              onClick={() => setShowAdditionalDropdown(!showAdditionalDropdown)}
            >
              <span className="ml-2">
                {/* {t(`filterOutputType.enum${fieldOutputType}`)} */}
                {t("apply_condition")}
              </span>
              <img src={svgIcons.ExpandIcon} className="size-6" />
            </button>
          )}
          {showAdditionalDropdown && (
            <div
              className={`absolute top-0 ${isLast ? "right-full mr-1" : "left-full ml-1"} flex flex-col gap-2 py-2 bg-white border border-border-gray rounded-lg shadow-lg w-64 z-popup`}
            >
              <FieldSpecificFilterOptions />
            </div>
          )}

          {/* Selectable Values List */}
          <>
            {/* Search Bar */}
            <div className="px-2">
              <AppSearchBox
                controlledValue={searchKeyword}
                initialValue=""
                placeholder={t("search_placeholder")}
                disabled={isFetchingColumnFilterDropdownValues}
                loading={false}
                onChange={(e) => setSearchKeyword(e.target.value || "")}
                onClear={() => setSearchKeyword("")}
              />
            </div>

            {/* Filter Options */}
            <div
              ref={itemsListRef}
              className="max-h-48 min-h-48 overflow-auto flex flex-col"
            >
              {!!itemsToDisplay && itemsToDisplay.length > 0 ? (
                itemsToDisplay.map((option: any, i: number) => {
                  const combinedValues = option.combinedValues;
                  const currentValue = `${option?.[identifier]}`;

                  // Your existing conversion logic
                  let convertedCellData = {
                    finalData: option?.[identifier],
                    isConverted: false,
                  };
                  try {
                    // skip conversion if aggregate function is COUNT
                    if (
                      column.field_object?.aggregate_function !==
                        AGGREGATE_FUNCTION.COUNT &&
                      !!column.field_output_type
                    ) {
                      convertedCellData = convertToUnit(
                        column.field_output_type,
                        option?.[identifier],
                        column?.convert_to_whole_number
                      );
                    }
                  } catch (error) {
                    if (APP_CONFIG.DEBUG.REPORTS) {
                      console.error("ERROR IN UNIT CONVERSION", error);
                      console.error("cellData", option?.[identifier]);
                      console.error("output_type", column.field_output_type);
                    }
                  }
                  // Determine the label to display based on the value
                  const value =
                    option?.[identifier] ===
                    REPORT_SPECIAL_VALUES.NULL.FOR_COMPARE // null
                      ? REPORT_SPECIAL_VALUES.NULL.FOR_DISPLAY // "N/A"
                      : option?.[identifier] ===
                          REPORT_SPECIAL_VALUES.BLANK.FOR_COMPARE // ""
                        ? REPORT_SPECIAL_VALUES.BLANK.FOR_DISPLAY // ""
                        : convertedCellData.finalData;
                  return (
                    <label
                      className="flex items-center gap-4 px-4 py-2 text-left leading-5 hover:bg-gray-200 cursor-pointer"
                      htmlFor={`${fieldId}_${identifier}_${i}`}
                      key={`${fieldId}_${identifier}_${i}`}
                    >
                      <TickCheckbox
                        id={`${fieldId}_${identifier}_${i}`}
                        isChecked={
                          combinedValues
                            ? combinedValues.some((val: string) =>
                                selectedOptions.includes(val)
                              )
                            : selectedOptions.includes(currentValue)
                        }
                        // isReadonly={!!selected && selected?.length > 0}
                        handleChange={(e) => {
                          if (combinedValues) {
                            setSelectedOptions((prev) =>
                              e.target.checked
                                ? [
                                    ...prev.filter(
                                      (item: string) =>
                                        !combinedValues.includes(item)
                                    ),
                                    ...combinedValues,
                                  ]
                                : prev.filter(
                                    (item: string) =>
                                      !combinedValues.includes(item)
                                  )
                            );
                          } else {
                            setSelectedOptions((prev) =>
                              e.target.checked
                                ? [...prev, currentValue]
                                : prev.filter((item) => item !== currentValue)
                            );
                          }
                        }}
                        wrapperClass="relative w-6 h-6 flex items-center justify-center shrink-0"
                      />
                      <DisplayConvertedValue
                        convertedCellData={{
                          ...convertedCellData,
                          finalData: value,
                        }}
                        originalData={option?.[identifier]}
                        wrapperClass="font-normal text-sm"
                      />
                    </label>
                  );
                })
              ) : (
                <div className="flex justify-center items-center h-48 text-black/40">
                  {tMain(
                    isFetchingColumnFilterDropdownValues
                      ? "loading_items"
                      : "no_items_found"
                  )}
                </div>
              )}
            </div>

            {/* footer buttons */}
            <div className="flex items-center gap-2 px-2">
              {/* disabled selected / total labels as this is causing issue with multiple column filters applied in unison */}
              {/* {!isFetchingColumnFilterDropdownValues && (
                <span className="text-black/50 text-xs">
                  {selectedOptions.length > 0
                    ? t("x_of_y_selected", {
                        x: selectedOptions.length,
                        y: itemsToDisplay.length
                      })
                    : t("total_items", { count: itemsToDisplay.length })}
                </span>
              )} */}
              <span className="flex-grow" />
              {isFetchingColumnFilterDropdownValues && (
                <img src={svgIcons.LoadingAnimation} className="size-8" />
              )}
              {showResetButton && (
                <AdminFormFieldSubmit
                  variant="primary-alt"
                  label={"Reset"}
                  type="button"
                  onClick={() => {
                    setSelectedOptions([]);
                    setSearchKeyword("");
                    // if values are selected, apply the filter
                    if (
                      selectedOptions.length > 0 &&
                      !!column.field_output_type
                    ) {
                      onApplyReportColumnFilter(
                        identifier,
                        column.field_output_type,
                        []
                      );
                    }
                  }}
                  disabled={
                    isFetchingColumnFilterDropdownValues ||
                    (selectedOptions.length === 0 && selected?.length === 0)
                  }
                />
              )}
              <AdminFormFieldSubmit
                label={"Apply"}
                variant="success"
                type="button"
                onClick={() => {
                  if (!column.field_output_type) return;
                  onApplyReportColumnFilter(
                    identifier,
                    column.field_output_type,
                    selectedOptions?.map((item) => {
                      const valueForAPI =
                        item === REPORT_SPECIAL_VALUES.NULL.FOR_COMPARE || // null
                        item === `${REPORT_SPECIAL_VALUES.NULL.FOR_COMPARE}` // "null"
                          ? REPORT_SPECIAL_VALUES.NULL.FOR_API // "NULL"
                          : item === REPORT_SPECIAL_VALUES.BLANK.FOR_COMPARE // ""
                            ? REPORT_SPECIAL_VALUES.BLANK.FOR_API // "BLANK"
                            : item;
                      return valueForAPI;
                    })
                  );
                }}
                disabled={
                  isFetchingColumnFilterDropdownValues ||
                  selectedOptions.length === 0
                }
              />
            </div>
          </>
        </>
      ) : (
        <FieldSpecificFilterOptions />
      )}
    </div>
  );
};

export default PopupReportColumnFilter;
